import type { IUserMe } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISettingValueMaster } from "@bbj-types/setting";
import { tz } from "@date-fns/tz";
import { getDateDeltaDays } from "@root/utils/date/delta";
import { startOfDay } from "date-fns";
import type { IModalActionForm, IModalActionResolver } from "./types";

export const hasConvocation = (
  candidate: Pick<ICandidate, "team"> | null,
  teamActive: boolean,
  actionType: ISettingValueMaster | null
) => {
  return Boolean(actionType?.metadata?.convocation && teamActive && candidate?.team?.showConvocation);
};
export const hasAddressRequired = (
  actionType: ISettingValueMaster | null,
  contactChannel: ISettingValueMaster | null
) => Boolean(actionType?.metadata?.actionAddress && contactChannel?.metadata?.actionAddress);

/**
 * L'adresse est également proposée (sans être obligatoire) lorsqu'une communication de rappel
 * est planifiée, afin d'alimenter la variable `action.address` du modèle envoyé au candidat.
 * Le switch n'étant rendu que sur les actions à faire, on s'aligne sur la même condition de
 * statut que le formatage de `shouldScheduleAppointmentReminder`.
 */
export const hasAddressDisplayed = (
  actionType: ISettingValueMaster | null,
  contactChannel: ISettingValueMaster | null,
  status: IModalActionForm["status"],
  shouldScheduleAppointmentReminder: boolean
) => hasAddressRequired(actionType, contactChannel) || (status === "todo" && shouldScheduleAppointmentReminder);

/**
 * The start time feeds the `action.startTime` variable of the reminder sent to the candidate, and
 * is needed to place the action in a calendar or bound its end time. Shared with the label so the
 * asterisk cannot drift away from the resolver rule.
 */
export const hasStartTimeRequired = (
  actionType: ISettingValueMaster | null,
  requiredContactDetails: string[],
  endTime: IModalActionForm["endTime"],
  sendEventToOwner: boolean,
  status: IModalActionForm["status"],
  shouldScheduleAppointmentReminder: boolean
) =>
  Boolean(endTime) ||
  sendEventToOwner ||
  (status === "todo" && shouldScheduleAppointmentReminder) ||
  requiredContactDetails.includes(actionType?.value ?? "");

export const buildSuccessMessage = (
  data: IModalActionResolver,
  user: IUserMe,
  timeZone: string,
  actionType?: string
) => {
  let message =
    data.candidate &&
    hasConvocation(data.candidate, user.organisation.features.team.active, data.type) &&
    getDateDeltaDays(data.date ?? new Date(), startOfDay(new Date(), { in: tz(timeZone) }), timeZone) >= 7
      ? "L'action a été enregistrée avec succès et la convocation sera envoyée dans les délais impartis"
      : "L'action a été enregistrée avec succès";

  const rule = user.organisation.coachingStepRules.action.find((rule) => rule.type === actionType);
  if (rule) {
    message = message.concat(". L'étape d'accompagnement du candidat a été mise à jour automatiquement");
  }
  return message;
};
